import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import './ProfileSkillsDrawer.css'

interface ProfileSkillsDrawerProps {
  isOpen: boolean
  manualSkills: string[]
  generatedSkills: string[]
  isSaving: boolean
  onClose: () => void
  onSave: (skills: string[]) => Promise<void>
  onClearCustomSkills: (() => Promise<void>) | null
}

const normalizeSkill = (value: string) => value.trim().replace(/\s+/g, ' ')

const dedupeSkills = (skills: string[]) => {
  const nextSkills: string[] = []
  const seen = new Set<string>()

  for (const rawSkill of skills) {
    const skill = normalizeSkill(rawSkill)
    if (!skill) {
      continue
    }

    const key = skill.toLowerCase()
    if (seen.has(key)) {
      continue
    }

    seen.add(key)
    nextSkills.push(skill)
  }

  return nextSkills
}

export const ProfileSkillsDrawer: React.FC<ProfileSkillsDrawerProps> = ({
  isOpen,
  manualSkills,
  generatedSkills,
  isSaving,
  onClose,
  onSave,
  onClearCustomSkills,
}) => {
  const [isMounted, setIsMounted] = useState(false)
  const [draftSkills, setDraftSkills] = useState<string[]>([])
  const [skillInput, setSkillInput] = useState('')

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const startingSkills = useMemo(() => manualSkills, [manualSkills])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    setDraftSkills(dedupeSkills(startingSkills))
    setSkillInput('')
  }, [isOpen, startingSkills])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const shouldLockBodyScroll =
      typeof window !== 'undefined' && window.innerWidth <= 768
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSaving) {
        onClose()
      }
    }

    const previousOverflow = document.body.style.overflow
    document.addEventListener('keydown', handleEscape)
    if (shouldLockBodyScroll) {
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen, isSaving, onClose])

  const addSkill = (candidate: string) => {
    const normalized = normalizeSkill(candidate)
    if (!normalized) {
      return
    }

    setDraftSkills((current) => dedupeSkills([...current, normalized]))
    setSkillInput('')
  }

  const removeSkill = (skillToRemove: string) => {
    setDraftSkills((current) =>
      current.filter((skill) => skill.toLowerCase() !== skillToRemove.toLowerCase()),
    )
  }

  const appendedGeneratedSkills = generatedSkills.filter(
    (skill) => !draftSkills.some((draftSkill) => draftSkill.toLowerCase() === skill.toLowerCase()),
  )

  const handleSubmit = async () => {
    await onSave(draftSkills)
  }

  if (!isOpen || !isMounted || typeof document === 'undefined' || !document.body) {
    return null
  }

  return createPortal(
    <div
      className="profile-skills-drawer-overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isSaving) {
          onClose()
        }
      }}
    >
      <aside
        className="profile-skills-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Edit profile skills"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="profile-skills-drawer__header">
          <div className="profile-skills-drawer__title">
            <h2>Edit Skills</h2>
            <p>
              Save custom skills for your profile. Resume-derived skills stay synced and are appended automatically.
            </p>
          </div>
          <button
            type="button"
            className="profile-skills-drawer__close"
            onClick={onClose}
            aria-label="Close skills drawer"
            disabled={isSaving}
          >
            ×
          </button>
        </div>

        <div className="profile-skills-drawer__content">
          <div className="profile-skills-drawer__section">
            <label htmlFor="profile-skill-input" className="profile-skills-drawer__label">
              Add a skill
            </label>
            <div className="profile-skills-drawer__input-row">
              <input
                id="profile-skill-input"
                type="text"
                value={skillInput}
                onChange={(event) => setSkillInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    addSkill(skillInput)
                  }
                }}
                className="profile-skills-drawer__input"
                placeholder="Type a skill and press Enter"
                disabled={isSaving}
              />
              <button
                type="button"
                className="btn btn-primary profile-skills-drawer__add"
                onClick={() => addSkill(skillInput)}
                disabled={isSaving || !normalizeSkill(skillInput)}
              >
                Add
              </button>
            </div>
          </div>

          <div className="profile-skills-drawer__section">
            <div className="profile-skills-drawer__section-header">
              <h3>Custom Profile Skills</h3>
              <span>{draftSkills.length} saved</span>
            </div>
            {draftSkills.length > 0 ? (
              <div className="profile-skills-drawer__chips">
                {draftSkills.map((skill) => (
                  <span key={skill} className="profile-skills-drawer__chip">
                    {skill}
                    <button
                      type="button"
                      onClick={() => removeSkill(skill)}
                      aria-label={`Remove ${skill}`}
                      disabled={isSaving}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="profile-skills-drawer__empty">
                No custom skills saved yet. Resume-derived skills will still be appended automatically.
              </p>
            )}
          </div>

          {generatedSkills.length > 0 && (
            <div className="profile-skills-drawer__section">
              <div className="profile-skills-drawer__section-header">
                <h3>Resume-Derived Skills</h3>
                <span>{generatedSkills.length} synced</span>
              </div>
              {appendedGeneratedSkills.length > 0 ? (
                <div className="profile-skills-drawer__chips profile-skills-drawer__chips--suggestions">
                  {appendedGeneratedSkills.map((skill) => (
                    <span key={skill} className="profile-skills-drawer__suggestion">
                      {skill}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="profile-skills-drawer__empty">
                  All resume-derived skills already overlap with your custom list.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="profile-skills-drawer__footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          {onClearCustomSkills && (
            <button
              type="button"
              className="btn btn-secondary profile-skills-drawer__reset"
              onClick={onClearCustomSkills}
              disabled={isSaving}
            >
              Clear Custom Skills
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Skills'}
          </button>
        </div>
      </aside>
    </div>,
    document.body,
  )
}
